import {from, of, Observable, Subscriber} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';

export default class DeviceLocationService {

    private static instance: DeviceLocationService;
    private lastLocationEvent: { date: number, coordinates: Coordinates } | null = null;

    private constructor() {
        this.subscribeOnEvents();
    }

    public static getInstance(): DeviceLocationService {
        if (!DeviceLocationService.instance) {
            DeviceLocationService.instance = new DeviceLocationService();
        }
        return DeviceLocationService.instance;
    }

    public isPermissionsGranted(): Observable<boolean> {
        let observable: Observable<boolean>;
        if (navigator.permissions && navigator.permissions.query) {
            observable = from(navigator.permissions.query({name: 'geolocation'})).pipe(
                map((status: PermissionStatus) => status.state === 'granted')
            );
        } else {
            observable = this.requestLocation().pipe(
                map((coordinates: Coordinates) => true),
                catchError(() => of(false))
            );
        }
        return observable;
    }

    public requestPermissions(): Observable<boolean> {
        return this.requestLocation().pipe(map((coordinates: Coordinates) => true));
    }

    public location(): Observable<Coordinates> {
        return this.isPermissionsGranted().pipe(
            mergeMap((granted: boolean) => {
                if (granted) {
                    return this.requestLocation();
                } else {
                    throw new Error('No permissions to get location');
                }
            })
        );
    }

    private requestLocation(): Observable<Coordinates> {
        return new Observable((subscriber: Subscriber<Coordinates>) => {
            if (this.lastLocationEvent) {
                subscriber.next(this.lastLocationEvent.coordinates);
                subscriber.complete();
            } else if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position: Position) => {
                        subscriber.next(position.coords);
                        subscriber.complete();
                    },
                    (error: PositionError) => {
                        subscriber.error(error);
                        subscriber.complete();
                    },
                    {enableHighAccuracy: false, timeout: 5000}
                );
            } else {
                subscriber.error('Cant get position');
                subscriber.complete();
            }
        });
    }

    private subscribeOnEvents(): void {
        document.addEventListener('currentPosition', (event: any) => {
            this.lastLocationEvent = {
                date: new Date().getTime(),
                coordinates: {
                    latitude: event.detail[0],
                    longitude: event.detail[1],
                    accuracy: 0,
                    altitude: 0,
                    altitudeAccuracy: 0,
                    heading: 0,
                    speed: 0,
                } as Coordinates
            };
        });
    }
}
